import PropTypes from 'prop-types';
import UtilityBar from '@ux/utility-bar';
import ApplicationFooter from '@ux/application-footer';
import AbstractHeader, { LazyBrowserDeprecationBanner } from '@ux/abstract-header';
import { events } from '@ux/header-util';
import React from 'react';
import Presets from './presets.json';
import { IntlProvider, FormattedMessage } from 'react-intl';

/**
 * Render and expose the Header as a functional Component.
 *
 * @param {Object} props Properties
 * @returns {ReactElement} Header.
 * @api public
 */
class Header extends AbstractHeader {
  constructor(props) {
    super(...arguments);

    const preset = Presets[props.preset];
    this.state = {
      ...this.state,
      unauthenticated: preset && this.isUnauthenticated(preset.components)
    };
    this.determineSkipToLinkHref = this.determineSkipToLinkHref.bind(this);
  }

  /**
   * Determines href string to set on a tag.
   *
   * @param {String} [id] String sent in by user.
   * @returns {String} Href to set on a tag or null if run on the server side
   */
  determineSkipToLinkHref(id) {
    if (id) {
      // If they provided an id, just let pass that string as href.
      return `#${id}`;
    }

    // fallback logic
    if (typeof window === 'undefined') {
      // Make sure we're okay for server rendering
      return;

    }
    const focusOn = ['#main-content', '#main', '#root'].find(sel => document.querySelector(sel));
    // If we found something better than our default use it, otherwise let the link handle it
    if (focusOn) {
      return focusOn;
    }

    return '#uxContent';
  }


  render() {
    const props = this.props;
    const cartProps = {
      items: this.state.cartItems,
      market: 'en-US',
      messages: props.messages,
      checkout: props.urls.checkout.href,
      eid: `uxp.hyd.${props.manifest}.header.cart.cart_link.link.click`,
      className: 'cart-link'
    };

    let preset = Presets[props.preset];
    if (!preset) {
      throw new Error(
        'Header preset not defined. This service serves the following headers: ' +
          Object.keys(Presets).join(', ')
      );
    }

    const plPreset = `${props.preset}-pl`;
    if (props.privateLabelId !== 1 && Presets[plPreset]) {
      preset = Presets[plPreset];
    }

    const components = preset.components || {};

    const skipToContentTitle = props.skipToMainContentLink && props.skipToMainContentLink.caption
      ? props.skipToMainContentLink.caption : <FormattedMessage id='Shared:Common:SkipToMainContent' />;
    const skipToContentSelector = this.determineSkipToLinkHref(props.skipToMainContentLink && props.skipToMainContentLink.id);

    return (
      <React.Fragment>
        <IntlProvider locale={ props.market } messages={ props.messages }>
          <div role='banner'>
            <a href={ skipToContentSelector } className='skip-to-main-content' >
              { skipToContentTitle }
            </a>
            <LazyBrowserDeprecationBanner
              supportMatrix={ props.supportMatrix }
              whitelistedUserAgents={ props.whitelistedUserAgents }
              disableDeprecationBanner={ props.disableDeprecationBanner }
              blacklistedBrowsers={ props.blacklistedBrowsers }
              urls={ props.urls } />
            <UtilityBar
              { ...props }
              { ...this.getCustomerState() }
              customer={ this.customer }
              components={ components }
              cart={ cartProps } />
            <FormattedMessage id='Shared:Common:MainContentStarts'>
              {text => (
                <span aria-label={ text } id='uxContent' role='navigation'></span>
              )}
            </FormattedMessage>
          </div>
        </IntlProvider>
      </React.Fragment>
    );
  }
}

Header.propTypes = {
  preset: PropTypes.oneOf(Object.keys(Presets)).isRequired,
  supportContacts: PropTypes.object,
  messages: PropTypes.object.isRequired,
  urls: PropTypes.object.isRequired,
  traffic: PropTypes.string,
  supportMatrix: PropTypes.object.isRequired,
  whitelistedUserAgents: PropTypes.array,
  disableDeprecationBanner: PropTypes.bool,
  blacklistedBrowsers: PropTypes.arrayOf(
    PropTypes.shape({
      browser: PropTypes.string,
      version: PropTypes.number
    })
  ),
  skipToMainContentLink: PropTypes.shape({
    id: PropTypes.string,
    caption: PropTypes.string
  })
};

/**
 * Render and expose the Footer as a functional Component.
 *
 * @param {Object} props Properties
 * @returns {ReactElement} Header.
 * @api public
 */
class Footer extends React.Component {
  componentDidMount() {
    events.emit('mount', 'footer', this);
    events.emit('mount:footer', this);
  }

  render() {
    const props = this.props;
    const preset = Presets[this.props.preset];

    if (!preset.footer) {
      return null;
    }

    return <ApplicationFooter { ...props } />;
  }
}

Footer.propTypes = {
  preset: PropTypes.oneOf(Object.keys(Presets)).isRequired
};

export { Header, Footer };
